(() => {
  if (customElements.get('dyefor-more-designs')) return;
  const clean = value => String(value || '').trim().toLowerCase();

  class MoreDesigns extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.products = JSON.parse(this.dataset.products);
        this.selectedOptions = JSON.parse(this.dataset.selectedOptions);
      } catch (_) { return; }
      this.picker = this.closest('variant-selects, variant-radios');
      this.productSection = this.closest('[id^="MainProduct-"]');
      this.list = this.querySelector('ul');
      this.expand = this.querySelector('[data-expand]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      if (!this.picker || !this.list || !this.products.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.expand.addEventListener('click', () => this.setExpanded(!this.classList.contains('is-expanded')), { signal });
      this.previous.addEventListener('click', () => this.scroll(-1), { signal });
      this.next.addEventListener('click', () => this.scroll(1), { signal });
      this.list.addEventListener('scroll', () => this.updateControls(), { signal, passive: true });
      this.picker.addEventListener('change', () => requestAnimationFrame(() => this.refreshSelection()), { signal });
      document.addEventListener('variantchanged', event => {
        const variant = event.detail;
        const formVariant = this.productSection?.querySelector('product-form [name="id"]')?.value;
        if (variant && String(variant.id) === String(formVariant)) {
          this.selectedOptions = variant.options;
          this.render();
        }
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.list);
      this.refreshSelection();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
    }

    refreshSelection() {
      if (!this.isConnected) return;
      const selects = [...this.picker.querySelectorAll('select[data-option]')];
      if (selects.length) this.selectedOptions = selects.map(select => select.value);
      else {
        const radios = [...this.picker.querySelectorAll('fieldset')];
        if (radios.length) this.selectedOptions = radios.map(fieldset => fieldset.querySelector(':checked')?.value || '');
      }
      this.render();
    }

    matchingVariant(product) {
      const devicePosition = Number(this.dataset.devicePosition);
      const device = clean(this.selectedOptions[devicePosition]);
      if (!device) return null;
      const styles = this.selectedOptions.filter((_, index) => index !== devicePosition).map(clean);
      const candidates = product.variants.filter(variant => variant.available
        && clean(variant.options[product.devicePosition]) === device);
      // Keep the case construction/colour too when this design offers it.
      return candidates.find(variant => variant.options.filter((_, index) => index !== product.devicePosition)
        .map(clean).join('|') === styles.join('|')) || candidates[0] || null;
    }

    render() {
      const selection = JSON.stringify(this.selectedOptions);
      if (selection === this.renderedSelection) return;
      this.renderedSelection = selection;
      const fragment = document.createDocumentFragment();
      const seen = new Set();
      const currentId = this.products[0].id;
      for (const product of this.products) {
        if (seen.has(product.id)) continue;
        seen.add(product.id);
        const variant = this.matchingVariant(product);
        const image = variant?.image || product.image;
        if (!variant || !image) continue;
        const url = new URL(product.url, location.origin);
        if (url.origin !== location.origin) continue;
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('device', variant.options[product.devicePosition]);
        const preview = new URLSearchParams(location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'dyefor-more-designs__item';
        link.href = url.pathname + url.search;
        link.title = product.title;
        link.setAttribute('aria-label', product.title);
        if (product.id === currentId) link.setAttribute('aria-current', 'page');
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.width = 84;
        img.height = 112;
        img.loading = 'lazy';
        img.decoding = 'async';
        link.append(img);
        li.append(link);
        fragment.append(li);
      }
      // Keep the server-rendered images in place when they already represent
      // this selection. Replacing them during startup can flash cached images.
      const existing = [...this.list.querySelectorAll('a')];
      const incoming = [...fragment.querySelectorAll('a')];
      const sameDesigns = existing.length === incoming.length && existing.every((link, index) => {
        const before = new URL(link.href);
        const after = new URL(incoming[index].href);
        return before.pathname === after.pathname
          && before.searchParams.get('variant') === after.searchParams.get('variant')
          && link.querySelector('img')?.src === incoming[index].querySelector('img')?.src;
      });
      if (sameDesigns) existing.forEach((link, index) => { link.href = incoming[index].href; });
      else this.list.replaceChildren(fragment);
      this.hidden = this.list.children.length < 2;
      this.list.scrollLeft = 0;
      this.updateControls();
    }

    setExpanded(expanded) {
      this.classList.toggle('is-expanded', expanded);
      this.expand.textContent = expanded ? 'Collapse' : 'Expand';
      this.expand.setAttribute('aria-expanded', String(expanded));
      this.updateControls();
    }

    scroll(direction) {
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollBy({ left: direction * this.list.clientWidth * .8, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    updateControls() {
      const expanded = this.classList.contains('is-expanded');
      const overflow = this.list.scrollWidth > this.list.clientWidth + 2;
      this.previous.hidden = expanded || !overflow;
      this.next.hidden = expanded || !overflow;
      this.expand.hidden = !expanded && !overflow;
      this.previous.disabled = this.list.scrollLeft <= 2;
      this.next.disabled = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 2;
    }
  }

  customElements.define('dyefor-more-designs', MoreDesigns);
})();
